/** Renders a blurry white streak burst behind the player at the moment a dash begins. */
import Phaser from 'phaser';
import { Component } from './Component';
import { CRISP_SCALE } from '../ui/CrispUtil';

const TRAIL_STREAK_MAX = 100;
const TRAIL_CANVAS_SIZE = (TRAIL_STREAK_MAX + 16) * 2 * CRISP_SCALE + 4;
const TRAIL_FADE_MS = 200;
const INV_CRISP = 1 / CRISP_SCALE;
const TRAIL_TEX_KEY = '_dash-trail';

/** Flashes a soft white blur burst behind the player on every dash, fading out over TRAIL_FADE_MS. */
export class DashTrailComponent implements Component {
  private scene: Phaser.Scene;
  private ownerSprite: Phaser.GameObjects.Sprite;
  private events: Phaser.Events.EventEmitter;

  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private image: Phaser.GameObjects.Image;
  private fadeTween: Phaser.Tweens.Tween | null = null;

  /** Public so Phaser tweens can target it directly. */
  alpha: number = 0;

  constructor(
    scene: Phaser.Scene,
    ownerSprite: Phaser.GameObjects.Sprite,
    events: Phaser.Events.EventEmitter
  ) {
    this.scene = scene;
    this.ownerSprite = ownerSprite;
    this.events = events;

    this.canvas = document.createElement('canvas');
    this.canvas.width = TRAIL_CANVAS_SIZE;
    this.canvas.height = TRAIL_CANVAS_SIZE;
    this.ctx = this.canvas.getContext('2d')!;

    if (!scene.textures.exists(TRAIL_TEX_KEY)) {
      scene.textures.addCanvas(TRAIL_TEX_KEY, this.canvas);
      scene.textures.get(TRAIL_TEX_KEY).setFilter(Phaser.Textures.FilterMode.LINEAR);
    }

    this.image = scene.add.image(0, 0, TRAIL_TEX_KEY)
      .setScale(INV_CRISP)
      .setVisible(false);

    this.events.on('dash-started', this.onDashStarted, this);
  }

  private onDashStarted(normX: number, normY: number): void {
    this.drawTrail(normX, normY);
    (this.scene.textures.get(TRAIL_TEX_KEY) as Phaser.Textures.CanvasTexture).refresh();

    if (this.fadeTween) {
      this.fadeTween.stop();
      this.fadeTween = null;
    }

    this.alpha = 0.9;
    this.image.setVisible(true);

    this.fadeTween = this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: TRAIL_FADE_MS,
      ease: 'Power2',
      onComplete: () => {
        this.image.setVisible(false);
        this.fadeTween = null;
      },
    });
  }

  update(_delta: number): void {
    if (!this.image.visible) return;
    this.image.setPosition(this.ownerSprite.x, this.ownerSprite.y);
    this.image.setAlpha(this.alpha);
    this.image.setDepth(this.ownerSprite.y - 1);
  }

  /** Draws 7 soft white streaks radiating backward from the dash direction. */
  private drawTrail(normX: number, normY: number): void {
    const S = CRISP_SCALE;
    const center = TRAIL_CANVAS_SIZE / 2;

    // Opposite to dash direction
    const backAngle = Math.atan2(-normY, -normX);

    const streaks = [
      { angleOffset: -1.22, length: 45,  alphaMult: 0.4  }, // -70°
      { angleOffset: -0.70, length: 70,  alphaMult: 0.65 }, // -40°
      { angleOffset: -0.26, length: 90,  alphaMult: 0.85 }, // -15°
      { angleOffset:  0,    length: 100, alphaMult: 1.0  }, // center
      { angleOffset:  0.26, length: 90,  alphaMult: 0.85 }, // +15°
      { angleOffset:  0.70, length: 70,  alphaMult: 0.65 }, // +40°
      { angleOffset:  1.22, length: 45,  alphaMult: 0.4  }, // +70°
    ];

    // Gaussian-style falloff via stacked wide-to-narrow passes
    const layers = [
      { lineWidth: 80, alphaScale: 0.03 },
      { lineWidth: 60, alphaScale: 0.05 },
      { lineWidth: 44, alphaScale: 0.08 },
      { lineWidth: 30, alphaScale: 0.12 },
      { lineWidth: 18, alphaScale: 0.2  },
      { lineWidth: 9,  alphaScale: 0.4  },
      { lineWidth: 3,  alphaScale: 0.75 },
    ];

    this.ctx.clearRect(0, 0, TRAIL_CANVAS_SIZE, TRAIL_CANVAS_SIZE);
    this.ctx.lineCap = 'round';

    for (const streak of streaks) {
      const a = backAngle + streak.angleOffset;
      const tailX = center + Math.cos(a) * streak.length * S;
      const tailY = center + Math.sin(a) * streak.length * S;

      for (const layer of layers) {
        const alpha = streak.alphaMult * layer.alphaScale;
        const grad = this.ctx.createLinearGradient(center, center, tailX, tailY);
        grad.addColorStop(0,   `rgba(255, 255, 255, ${alpha})`);
        grad.addColorStop(0.5, `rgba(220, 235, 255, ${alpha * 0.6})`);
        grad.addColorStop(1,   `rgba(200, 220, 255, 0)`);

        this.ctx.strokeStyle = grad;
        this.ctx.lineWidth = layer.lineWidth * S;
        this.ctx.beginPath();
        this.ctx.moveTo(center, center);
        this.ctx.lineTo(tailX, tailY);
        this.ctx.stroke();
      }
    }
  }

  destroy(): void {
    this.events.off('dash-started', this.onDashStarted, this);
    if (this.fadeTween) {
      this.fadeTween.stop();
    }
    this.image.destroy();
    if (this.scene.textures.exists(TRAIL_TEX_KEY)) {
      this.scene.textures.remove(TRAIL_TEX_KEY);
    }
  }
}
